import store from '@/store';
import { AutocompleteItem, Model } from '@/models/Model';
import Avatar from '@/support/Avatar';
import Token from '@/support/Token';
import { RoleKey } from '@/support/RoleInfo';

export class User extends Model {
  protected $name = 'User';

  protected $endpoint = '/v1/users';

  protected $primaryKey = 'id';

  protected $fillable: string[] = [];

  public id?: string;

  public email?: string;

  public phone?: string;

  public name?: string;

  public first_name?: string;

  public last_name?: string;

  public avatar?: string;

  public role?: RoleKey;

  public password?: string;

  public confirm_password?: string;

  public status?: string;

  public active?: string;

  public hin_type?: string;

  public has_hin!: boolean;

  public mfa_token!: boolean;

  public force_password_change!: boolean;

  public uuid?: string;

  public mobile?: string | null;

  public address?: {
    postcode: '',
    number: '',
    number_add: '',
    city: '',
    street: '',
  };

  public get userName(): string {
    if (this.name) { return this.name; }
    if (this.email) { return this.email; }
    return 'user@example.com';
  }

  public get userAvatar(): string {
    if (this.avatar) { return this.avatar; }

    return new Avatar(this.userName).toDataUrl();
  }

  public current(): Promise<User> {
    return this.filter('current', true)
      .include('contractor')
      .first();
  }

  public storeQr(attributes?: object, id?: string): Promise<any> {
    const url = `${this.$endpoint}/${id}/set-mfa`;
    attributes = this.getPayload(attributes);

    return this.request('post', url, attributes);
  }

  public getQR(id: any): Promise<any> {
    const url = `${this.$endpoint}/${id}/get-mfa`;

    return this.request('get', url);
  }

  public deleteMfa(id: any): Promise<any> {
    const url = `${this.$endpoint}/${id}/mfa`;

    return this.request('delete', url);
  }

  public static me(): User|null {
    return store.getters.user;
  }

  public hasRole(roles: RoleKey | RoleKey[]): boolean {
    if (this.role === undefined) {
      return false;
    }

    if (! Array.isArray(roles)) {
      roles = [roles];
    }

    let hasRole = false;
    if (roles.includes(this.role)) {
      hasRole = true;
    }

    return hasRole;
  }

  public hasHinType(type: string): boolean {
    if (this.hin_type === undefined) {
      return false;
    }

    let hasType = false;
    if (type === this.hin_type) {
      hasType = true;
    }

    return hasType;
  }

  public logout(): void {
    Token.delete(['access', 'refresh']);
  }

  public get autocompleteItem(): AutocompleteItem {
    return {
      value: this.id || '',
      text: this.name || '',
      name: this.getInstanceName(),
    };
  }

  public resolvePrimaryKey(): string {
    return this.uuid || '';
  }
}

export interface ReportTab {
  name: string;
  value: string;
}

export const userReportTabs: ReportTab[] = [
  {
    name: 'Communication',
    value: 'communication',
  },
  {
    name: 'Tasks',
    value: 'tasks',
  },
];

export interface CreateUserPayload {
  email?: string;
  name?: string;
  password?: string;
  confirm_password?: string;
  role?: RoleKey;
  status?: string;
  hin_type?: string;
  contractor?: string;
}

export interface UserUpdatePayload {
  name?: string;
  email?: string;
  password?: string;
  confirm_password?: string;
  active?: string;
}

export type UserDeleteManyPayload = {
  users?: string[];
}

export type UserAddPayload = {
  user?: string;
}

export type UserRoleItem = {
  name: string;
  key: RoleKey;
  description: string;
}
